import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Pagination from "@mui/material/Pagination";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import GameIcon from "components/admin/GameIcon";
import { useFetch, useRunner } from "components/admin/hooks";
import api, { assetUrl } from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";

const PAGE_SIZE = 60;

function IconGallery({ folder, value, onPick, onClose }) {
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const { data, loading, error } = useFetch(
    () => api.get("/api/admin/templates/icons", { folder, q, page, size: PAGE_SIZE }),
    [folder, q, page]
  );
  const pages = Math.max(1, Math.ceil((data?.total || 0) / PAGE_SIZE));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Chọn icon</DialogTitle>
      <DialogContent>
        <MDBox
          component="form"
          display="flex"
          gap={1}
          pt={1}
          mb={2}
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setQ(input.trim());
          }}
        >
          <MDInput
            fullWidth
            label="Tìm theo tên vật phẩm / pet đang dùng icon, hoặc tên file"
            value={input}
            onChange={(event) => setInput(event.target.value)}
          />
          <MDButton type="submit" variant="gradient" color="info">
            <Icon>search</Icon>
          </MDButton>
        </MDBox>
        {error && (
          <MDTypography variant="caption" color="error">
            {error}
          </MDTypography>
        )}
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </MDBox>
        ) : (
          <MDBox display="grid" gridTemplateColumns="repeat(auto-fill, minmax(76px, 1fr))" gap={1}>
            {(data?.items || []).map((icon) => (
              <Tooltip
                key={icon.path}
                title={
                  <MDBox textAlign="left">
                    <MDTypography variant="caption" color="white" display="block">
                      {icon.path}
                    </MDTypography>
                    {icon.usedBy.length > 0 && (
                      <MDTypography variant="caption" color="white">
                        Đang dùng: {icon.usedBy.join(", ")}
                      </MDTypography>
                    )}
                  </MDBox>
                }
              >
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  p={1}
                  borderRadius="md"
                  onClick={() => onPick(icon.path)}
                  sx={{
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor: icon.path === value ? "info.main" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <GameIcon path={icon.path} size={40} />
                  <MDTypography
                    variant="caption"
                    color="text"
                    noWrap
                    sx={{ maxWidth: "100%", fontSize: 10 }}
                  >
                    {icon.usedBy[0] || icon.path.split("/").pop()}
                  </MDTypography>
                </MDBox>
              </Tooltip>
            ))}
          </MDBox>
        )}
        {!loading && data?.items?.length === 0 && (
          <MDTypography variant="caption" color="text">
            Không tìm thấy icon
          </MDTypography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Pagination count={pages} page={page} onChange={(event, p) => setPage(p)} size="small" />
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Đóng
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

IconGallery.propTypes = {
  folder: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onPick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const SIZE_OPTIONS = [20, 24, 30, 40, 48, 64];
const MAX_BYTES = 2 * 1024 * 1024;

/** Đọc kích thước thật của ảnh (icon hiện tại hoặc file vừa chọn). */
function useImageSize(src) {
  const [size, setSize] = useState(null);
  useEffect(() => {
    setSize(null);
    if (!src) return undefined;
    let active = true;
    const img = new Image();
    img.onload = () => active && setSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = src;
    return () => {
      active = false;
    };
  }, [src]);
  return size;
}

function fitSize(size, max) {
  if (!size) return null;
  if (!max || (size.width <= max && size.height <= max)) return size;
  const scale = Math.min(max / size.width, max / size.height);
  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale)),
  };
}

function UploadDialog({ folder, current, onUploaded, onClose }) {
  const [run, busy] = useRunner();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const currentSize = useImageSize(current ? assetUrl(current) : null);
  const fileSize = useImageSize(preview);
  const currentMax = currentSize ? Math.max(currentSize.width, currentSize.height) : null;
  const [maxSize, setMaxSize] = useState("current");

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const pick = (picked) => {
    if (!picked) return;
    if (!picked.type.startsWith("image/")) {
      setError("File phải là ảnh (PNG, JPG, GIF, BMP)");
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError("Ảnh tối đa 2MB");
      return;
    }
    setError(null);
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
  };

  let target = 0;
  if (maxSize === "current") target = currentMax || 30;
  else if (maxSize !== "original") target = Number(maxSize);
  const output = fitSize(fileSize, target || 256);

  const submit = async () => {
    const { ok, result } = await run(
      () => api.upload("/api/admin/templates/icons/upload", { file, folder, maxSize: target }),
      (res) => `Đã tải ảnh lên (${res.width}x${res.height})`
    );
    if (ok) onUploaded(result.path);
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Tải ảnh từ máy</DialogTitle>
      <DialogContent>
        <MDBox
          component="label"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={1}
          mt={1}
          p={3}
          borderRadius="lg"
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            pick(event.dataTransfer.files?.[0]);
          }}
          sx={{
            cursor: "pointer",
            border: "2px dashed",
            borderColor: dragging ? "info.main" : "divider",
            bgcolor: dragging ? "action.hover" : "transparent",
          }}
        >
          <input
            hidden
            type="file"
            accept="image/png,image/jpeg,image/gif,image/bmp"
            onChange={(event) => pick(event.target.files?.[0])}
          />
          {preview ? (
            <MDBox
              component="img"
              src={preview}
              alt=""
              sx={{ maxWidth: 160, maxHeight: 160, objectFit: "contain" }}
            />
          ) : (
            <Icon fontSize="large">cloud_upload</Icon>
          )}
          <MDTypography variant="button" color="text">
            {file ? file.name : "Bấm để chọn ảnh hoặc kéo thả ảnh vào đây"}
          </MDTypography>
          {fileSize && (
            <MDTypography variant="caption" color="text">
              Ảnh gốc {fileSize.width}x{fileSize.height}
            </MDTypography>
          )}
        </MDBox>
        {error && (
          <MDTypography variant="caption" color="error" display="block" mt={1}>
            {error}
          </MDTypography>
        )}

        <MDInput
          select
          fullWidth
          label="Kích thước trong game"
          value={maxSize}
          onChange={(event) => setMaxSize(event.target.value)}
          SelectProps={{ sx: { height: 44 } }}
          sx={{ mt: 2 }}
          helperText="Icon vật phẩm trong game thường chỉ 20-32px, ảnh lớn sẽ được thu nhỏ vừa khung"
        >
          <MenuItem value="current">
            {currentSize
              ? `Bằng icon hiện tại (${currentSize.width}x${currentSize.height})`
              : "Bằng icon hiện tại (30px)"}
          </MenuItem>
          {SIZE_OPTIONS.map((s) => (
            <MenuItem key={s} value={String(s)}>
              Tối đa {s}px
            </MenuItem>
          ))}
          <MenuItem value="original">Giữ nguyên (tối đa 256px)</MenuItem>
        </MDInput>

        {preview && output && (
          <MDBox display="flex" alignItems="flex-end" gap={3} mt={2}>
            <MDBox textAlign="center">
              <MDBox
                component="img"
                src={preview}
                alt=""
                sx={{ width: output.width, height: output.height, objectFit: "contain" }}
              />
              <MDTypography variant="caption" color="text" display="block">
                Thật ({output.width}x{output.height})
              </MDTypography>
            </MDBox>
            <MDBox textAlign="center">
              <MDBox
                component="img"
                src={preview}
                alt=""
                sx={{
                  width: output.width * 3,
                  height: output.height * 3,
                  objectFit: "contain",
                  imageRendering: "pixelated",
                }}
              />
              <MDTypography variant="caption" color="text" display="block">
                Phóng to x3
              </MDTypography>
            </MDBox>
            {current && (
              <MDBox textAlign="center">
                <GameIcon path={current} size={Math.max(output.width, output.height) * 3} />
                <MDTypography variant="caption" color="text" display="block">
                  Icon cũ x3
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={busy}>
          Huỷ
        </MDButton>
        <MDButton variant="gradient" color="info" disabled={!file || busy} onClick={submit}>
          {busy ? "Đang tải..." : "Tải lên và dùng ảnh này"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

UploadDialog.defaultProps = { current: "" };
UploadDialog.propTypes = {
  folder: PropTypes.string.isRequired,
  current: PropTypes.string,
  onUploaded: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/** Ô chọn icon từ thư mục assets của game hoặc tải ảnh từ máy, xem trước ảnh thay vì gõ đường dẫn. */
function IconPicker({ folder, value, onChange, label }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const canUpload = useCan(PERMISSIONS.TEMPLATE_EDIT);
  return (
    <MDBox display="flex" alignItems="center" gap={2}>
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={64}
        height={64}
        borderRadius="md"
        sx={{ bgcolor: "action.hover" }}
      >
        <GameIcon path={value} size={48} />
      </MDBox>
      <MDBox>
        <MDTypography variant="button" fontWeight="medium" display="block">
          {label}
        </MDTypography>
        <MDTypography variant="caption" color="text" display="block" mb={0.5}>
          {value || "Chưa có icon"}
        </MDTypography>
        <MDBox display="flex" gap={1} flexWrap="wrap">
          <MDButton size="small" variant="outlined" color="info" onClick={() => setOpen(true)}>
            <Icon>image_search</Icon>&nbsp;Chọn icon có sẵn
          </MDButton>
          {canUpload && (
            <MDButton
              size="small"
              variant="outlined"
              color="success"
              onClick={() => setUploading(true)}
            >
              <Icon>upload</Icon>&nbsp;Tải ảnh từ máy
            </MDButton>
          )}
        </MDBox>
      </MDBox>
      {uploading && (
        <UploadDialog
          folder={folder}
          current={value}
          onClose={() => setUploading(false)}
          onUploaded={(path) => {
            onChange(path);
            setUploading(false);
          }}
        />
      )}
      {open && (
        <IconGallery
          folder={folder}
          value={value}
          onClose={() => setOpen(false)}
          onPick={(path) => {
            onChange(path);
            setOpen(false);
          }}
        />
      )}
    </MDBox>
  );
}

IconPicker.defaultProps = { value: "", label: "Icon" };

IconPicker.propTypes = {
  folder: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default IconPicker;
